import { ArrowLeft, Bell, BellRing, Columns3, Heart, Wallet, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Modal } from "../../components/Modal";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { TripProductCard } from "../../components/trips/TripProductCard";
import { track } from "../../lib/analytics";
import { formatCalendarDate, relativeTime } from "../../lib/format";
import { setAllSavedTripAlerts, setSavedTripAlert, useSavedTrips } from "../../lib/queries";
import { amountWithCurrency } from "../../lib/reservations";
import { COMPARE_MAX, SORT_LABELS, applyFilter, collectionSummary, compareRows, filterOptions, sortSaved, type SavedFilter, type SavedSort } from "../../lib/saved-trips";
import type { SavedTrip } from "../../types";

/**
 * "Viajes guardados" (design turno 6): the collection behind the heart. Desktop: a grid of three
 * with region filters, price alerts and a comparison of up to three trips; mobile: one wide card
 * per row with the filled heart to remove. The wallet block appears only where one exists.
 */
export function SavedTripsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const saved = useSavedTrips();
  const [filter, setFilter] = useState<SavedFilter>("all");
  const [sort, setSort] = useState<SavedSort>("recent");
  const [comparing, setComparing] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const items = saved.data ?? [];
  const summary = useMemo(() => collectionSummary(items), [items]);
  const options = useMemo(() => filterOptions(items), [items]);
  const visible = useMemo(() => sortSaved(applyFilter(items, filter), sort), [items, filter, sort]);
  const allAlerts = items.length > 0 && items.every((item) => item.priceAlert);
  const compared = picked.map((id) => items.find((item) => item.tripId === id)).filter((item): item is SavedTrip => Boolean(item));

  usePageHeader("Viajes guardados", saved.data
    ? items.length
      ? [`${summary.trips} ${summary.trips === 1 ? "viaje" : "viajes"}`, `${summary.destinations} ${summary.destinations === 1 ? "destino" : "destinos"}`, summary.lastSavedAt ? `último guardado ${relativeTime(summary.lastSavedAt)}` : null].filter(Boolean).join(" · ")
      : "Tu colección de viajes"
    : undefined);

  // A filter that no longer applies (the last trip of a region was removed) falls back to Todos.
  useEffect(() => { if (!options.some((option) => option.id === filter)) setFilter("all"); }, [options, filter]);
  useEffect(() => { if (saved.data) track("saved_trips_viewed", { active_filter: filter, result_count: visible.length }); }, [saved.data, filter, visible.length]);
  useEffect(() => { setPicked((current) => current.filter((id) => items.some((item) => item.tripId === id))); }, [items]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["saved-trips"] });
  const toggleAll = async () => {
    setBusy(true); setNote(null);
    try {
      await setAllSavedTripAlerts(!allAlerts);
      setNote(!allAlerts ? "Te avisaremos por correo si alguno de tus viajes guardados baja de precio." : "Avisos de precio desactivados.");
    } catch { setNote("No pudimos actualizar los avisos. Intenta de nuevo."); }
    finally { setBusy(false); await refresh(); }
  };
  const toggleOne = async (item: SavedTrip, next: boolean) => {
    setNote(null);
    try { await setSavedTripAlert(item.tripId, next); } catch { setNote("No pudimos actualizar el aviso. Intenta de nuevo."); }
    await refresh();
  };
  const pick = (id: string) => setPicked((current) => current.includes(id) ? current.filter((value) => value !== id) : current.length >= COMPARE_MAX ? current : [...current, id]);

  if (saved.isLoading) return <div className="page-state"><Spinner label="Cargando guardados" /><span>Cargando tus viajes guardados</span></div>;
  if (saved.isError) return <div className="page-state error"><strong>No pudimos cargar tus guardados</strong><button className="secondary-button" onClick={() => void saved.refetch()}>Reintentar</button></div>;

  if (!items.length) {
    return (
      <div className="page-container">
        <button type="button" className="back-button" onClick={() => navigate("/explorar")}><ArrowLeft size={16} aria-hidden="true" /> Explorar</button>
        <div className="empty-state tall">
          <Heart size={30} aria-hidden="true" />
          <strong>Aún no guardas viajes</strong>
          <span>Toca el corazón en cualquier viaje de la sección Viajes y aparecerá aquí, con su precio y sus próximas salidas.</span>
          <button type="button" className="primary-button solid" onClick={() => navigate("/explorar")}>Explorar viajes</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container saved-page">
      <button type="button" className="back-button" onClick={() => navigate("/explorar")}><ArrowLeft size={16} aria-hidden="true" /> Explorar</button>
      <div className="saved-head">
        <div>
          <span className="page-eyebrow">Tu colección</span>
          <h2>Viajes guardados</h2>
          <span className="request-meta">{summary.trips} {summary.trips === 1 ? "viaje" : "viajes"}{summary.withWallet ? ` · ${summary.withWallet} con wallet ${summary.withWallet === 1 ? "activa" : "activas"}` : ""}</span>
        </div>
        <div className="inline-actions">
          <button type="button" className={`secondary-button${allAlerts ? " on" : ""}`} onClick={() => void toggleAll()} disabled={busy} aria-pressed={allAlerts}>
            {allAlerts ? <BellRing size={16} aria-hidden="true" /> : <Bell size={16} aria-hidden="true" />} {allAlerts ? "Avisos de precio activos" : "Avisarme si baja el precio"}
          </button>
          <button type="button" className={comparing ? "secondary-button" : "primary-button solid"} onClick={() => { setComparing((value) => !value); setPicked([]); }} aria-pressed={comparing} disabled={items.length < 2}>
            {comparing ? <X size={16} aria-hidden="true" /> : <Columns3 size={16} aria-hidden="true" />} {comparing ? "Cancelar comparación" : "Comparar viajes"}
          </button>
        </div>
      </div>

      <div className="saved-toolbar">
        <div className="chip-row" role="group" aria-label="Filtrar guardados">
          {options.map((option) => (
            <button key={option.id} type="button" className={`chip${filter === option.id ? " active" : ""}`} aria-pressed={filter === option.id} onClick={() => setFilter(option.id)}>
              {option.id === "wallet" && <Wallet size={14} aria-hidden="true" />} {option.label} <span className="chip-count" translate="no">{option.count}</span>
            </button>
          ))}
        </div>
        <label className="sort-select">
          <span>Ordenar:</span>
          <select value={sort} onChange={(event) => setSort(event.target.value as SavedSort)} aria-label="Ordenar guardados">
            {(Object.keys(SORT_LABELS) as SavedSort[]).map((key) => <option key={key} value={key}>{SORT_LABELS[key]}</option>)}
          </select>
        </label>
      </div>
      {note && <div className="form-message" role="status">{note}</div>}
      {comparing && <p className="page-note">Elige hasta {COMPARE_MAX} viajes para compararlos lado a lado.</p>}

      {visible.length ? (
        <div className="trip-card-grid saved-grid">
          {visible.map((item) => (
            <TripProductCard
              key={item.tripId} trip={item.trip} saved={item} placement="saved" onClick={() => navigate(`/viajes/${item.trip.slug}`)}
              selectable={comparing} selected={picked.includes(item.tripId)} onSelect={() => pick(item.tripId)} onToggleAlert={(next) => void toggleOne(item, next)}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state"><strong>Sin viajes con ese filtro</strong><span>Prueba otra región o quita el filtro.</span><button type="button" className="secondary-button small" onClick={() => setFilter("all")}>Ver todos</button></div>
      )}

      {comparing && (
        <div className="compare-bar" role="status">
          <span>{picked.length} de {COMPARE_MAX} seleccionados</span>
          <button type="button" className="primary-button solid small" disabled={picked.length < 2} onClick={() => setCompareOpen(true)}><Columns3 size={14} aria-hidden="true" /> Ver comparación</button>
        </div>
      )}

      <Modal open={compareOpen} onOpenChange={setCompareOpen} label="Comparación de viajes">
        <div className="dialog-content wide compare-dialog">
          <button type="button" className="dialog-close" onClick={() => setCompareOpen(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
          <span className="page-eyebrow">Comparar viajes</span>
          <h2>Lado a lado</h2>
          <div className="table-wrap">
            <table className="trip-table compare-table">
              <thead><tr><th scope="col" />{compared.map((item) => <th key={item.tripId} scope="col">{item.trip.name}</th>)}</tr></thead>
              <tbody>
                {compareRows(compared, { price: amountWithCurrency, date: (value) => (value ? formatCalendarDate(value) : "") }).map((row) => (
                  <tr key={row.label}><th scope="row">{row.label}</th>{row.values.map((value, index) => <td key={index} translate={row.label.startsWith("Desde") ? "no" : undefined}>{value}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="inline-actions">
            {compared.map((item) => <button key={item.tripId} type="button" className="secondary-button small" onClick={() => navigate(`/viajes/${item.trip.slug}`)}>Ver {item.trip.name}</button>)}
          </div>
        </div>
      </Modal>
    </div>
  );
}
